import { ArrowDropDownOutlined, Check } from '@mui/icons-material';
import dayjs from 'dayjs';
import UserHandler from 'handlers/internal.user.handler';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import tz from 'dayjs/plugin/timezone';
import { ReloadDialog } from 'components/common/ReloadDialog';
import { ThemePalette } from 'mui.theme';
import { isDarkTheme } from 'utils/theme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
dayjs.extend(tz);

const TimezoneMenu = () => {
  const { t } = useTranslation();
  const userHandler = new UserHandler();
  const { timezone, preferredTimeDisplayMode, userReducerLocation } =
    useTimeDisplayMode();
  const authUserEmail = useAppSelector((state) => state.authReducer.email);
  const { internalUserList } = useAppSelector(
    (state) => state.internalUserReducer
  );
  const { externalUserList } = useAppSelector(
    (state) => state.externalUserReducer
  );
  const { role, customer } = useAppSelector((state) => state.authReducer);
  const enableDarkTheme = isDarkTheme(customer.theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { customerCode } = useCustomerInfo();
  // Initialize state with the customer's preferred timezone or a default
  const [
    customerpreferredTimeDisplayMode,
    setCustomerpreferredTimeDisplayMode,
  ] = useState(
    preferredTimeDisplayMode ?? 'local' // Fallback to first option if undefined
  );
  const [showDialog, setShowDialog] = useState(false);

  // Handler for when a timezone option is clicked
  // It now accepts the timezone ID directly as an argument
  const handleTimezoneChange = async (
    newTimeZoneId: preferredTimeDisplayModeTypes
  ) => {
    // Only proceed if the timezone is actually changing
    if (newTimeZoneId === customerpreferredTimeDisplayMode) {
      return;
    }

    try {
      const selectedUser = [...internalUserList, ...externalUserList].find(
        (user) => user.email === authUserEmail
      ) ?? {
        userId: '',
        firstName: '',
        lastName: '',
        email: '',
        isActive: true,
        department: '',
        userCustomerAssociations: [],
      };

      if (!role) return;

      await userHandler.updatePreferredTimeDisplayMode(
        selectedUser.userId,
        newTimeZoneId,
        userReducerLocation
      );

      // Only show the dialog if the timezone actually changed
      setCustomerpreferredTimeDisplayMode(newTimeZoneId);
      setShowDialog(true);
    } catch (error) {
      console.error('Error updating preferred timezone:', error);
    }
  };

  // Timezone options for the selector
  const preferredTimeDisplayModeOptions: Array<{
    id: preferredTimeDisplayModeTypes;
    display: string;
  }> = useMemo(() => {
    const options = [
      // { id: 'utc', display: 'UTC' },
      { id: 'local', display: dayjs.tz.guess() },
      { id: 'configured', display: timezone },
      // { id: 'utc', display: 'UTC' },
    ];
    if (options[0].display === options[1].display) {
      options.shift();
    }

    // Temp remove 'local' for buz
    const filteredOptions = ['buz', 'myn'].includes(customerCode)
      ? options.filter((opt) => opt.id !== 'local')
      : options;

    return filteredOptions as Array<{
      id: preferredTimeDisplayModeTypes;
      display: string;
    }>;
  }, [timezone, customerCode]);

  const displayTimezone = useMemo(() => {
    switch (customerpreferredTimeDisplayMode) {
      case 'utc':
        return 'UTC';
      case 'local':
        return dayjs.tz.guess();
      case 'configured':
        return timezone;
      default:
        return 'UTC';
    }
  }, [customerpreferredTimeDisplayMode, timezone]);

  useEffect(() => {
    if (['buz', 'myn'].includes(customerCode)) {
      // temp added configured to be always for buz user
      setCustomerpreferredTimeDisplayMode('configured');
    } else {
      setCustomerpreferredTimeDisplayMode(preferredTimeDisplayMode);
    }
  }, [preferredTimeDisplayMode, customerCode]);

  return (
    <>
      <div className="timezone-selector">
        <div className="timezone-dropdown">
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clipRule="evenodd"
            />
          </svg>
          <span id="selected-timezone">{displayTimezone}</span>
          <ArrowDropDownOutlined fontSize="small" />
        </div>
        <div
          className="timezone-menu"
          style={{
            background: enableDarkTheme
              ? palette.menuOptions
              : palette.boxBackground,
            borderColor: palette.cardBorderColor,
          }}
        >
          {/* <div className="timezone-option active">
        <span>{currentSelectedOptionDisplay}</span>
      </div> */}
          {preferredTimeDisplayModeOptions.map((option) => {
            const isActive = option.id === customerpreferredTimeDisplayMode;
            return (
              <div
                key={option.id}
                className={`timezone-option ${isActive ? 'active' : ''}`}
                style={{
                  backgroundColor: isActive
                    ? enableDarkTheme
                      ? palette.boxBackground
                      : palette.boxBackground
                    : 'transparent',
                  color: isActive
                    ? palette.textPrimary
                    : enableDarkTheme
                    ? ThemePalette.typography.lightGrey
                    : ThemePalette.typography.darkGrey,
                }}
                onClick={async () => {
                  await handleTimezoneChange(option.id);
                }}
              >
                <span>{option.display}</span>
                {isActive && <Check fontSize="small" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* App Reload Dialog */}
      <ReloadDialog
        open={showDialog}
        message={t(
          'you_have_updated_the_timezone_the_app_will_reload_to_apply_c'
        )}
        buttonText={t('reload_now')}
      />
    </>
  );
};

export default TimezoneMenu;
